import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Input from "../../../../../components/Input";
import Dropdown from "../../../../../components/Dropdown";
import { VALIDATION_FIELD_MAPPING } from '../../../../../utils/appConstant';
import { ListFilters } from "../../../../../interfaces/user.interface";
import useDebounce from "../../../../../hooks/useDebounce";

interface FilterProps {
  roles: any[]
  managers: any[]
  allRoles: any[];
  allManagers: any[];
  status: any[];
  allStatus: any[];
  dropdownSearchData: any;
  onDropdownSearchChange: (e: any, name: string) => void;
  initialFilters: ListFilters;
  onApply: (filters: ListFilters) => void;
  onReset: () => void;
}

const Filter: React.FC<FilterProps> = ({ allRoles, allManagers, roles, managers, status, allStatus, dropdownSearchData, onDropdownSearchChange, initialFilters, onApply, onReset }) => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<ListFilters>(initialFilters);
  const debouncedSearchText = useDebounce(filters.searchText, 500);

  // Calculate initial filtersApplied state
  const isApplied = filters.searchText !== '' ||
    filters.role !== '' ||
    filters.manager !== '' ||
    filters.status !== '1';

  const [filtersApplied, setFiltersApplied] = useState(false);

  // Sync state with props when they change (e.g. on Reset from parent if that happened, though we handle reset locally too)
  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  // Effect to handle Real-time application
  useEffect(() => {
    // Construct the filters object to apply
    const filtersToApply = {
      ...filters,
      searchText: debouncedSearchText
    };

    // Determine if filters are applied
    const applied = filtersToApply.searchText !== '' ||
      filtersToApply.role !== '' ||
      filtersToApply.manager !== '' ||
      filtersToApply.status !== '1';

    setFiltersApplied(applied);

    // Call onApply
    onApply(filtersToApply);
  }, [debouncedSearchText, filters.role, filters.manager, filters.status, onApply]);

  const handleInputChange = useCallback((event: any, name: string) => {
    setFilters((prev: ListFilters) => ({ ...prev, [name]: event.target.value }));
  }, []);

  const handleReset = () => {
    const defaults: ListFilters = {
      searchText: '',
      role: '',
      status: '1',
      manager: ''
    };
    setFilters(defaults);
    onReset();
  };

  return (
    <div className={`card mb-4 ${filtersApplied ? 'border border-primary border-2' : ''}`}>
      <div className="card-body">
        <div className="col-12 mb-3 d-flex justify-content-between align-items-center">
          <h5 className="m-0 text-dark">{t("common.filters")}</h5>
          {filtersApplied && (
            <small
              onClick={handleReset}
              className="cursor-pointer text-primary"
            >
              {t("common.clearFilters")}
            </small>
          )}
        </div>

        <div className="col-12">
          <div className="row g-3">
            <div className="col-md-6 col-sm-12">
              <Input
                type="search"
                id={VALIDATION_FIELD_MAPPING.searchText}
                name={VALIDATION_FIELD_MAPPING.searchText}
                placeholder={t("users.list.pageTexts.filters.searchPlaceholder")}
                value={filters.searchText}
                onChange={handleInputChange}
                autoComplete="on"
              />
            </div>
            <div className="col-md-2 col-sm-4">
              <Dropdown
                values={filters.manager && filters.manager.toString().trim() ? [filters.manager] : []}
                options={(managers || []).map((manager: any) => ({
                  label: manager.name,
                  value: manager.id,
                }))}
                allOptions={(allManagers || []).map((manager: any) => ({
                  label: manager.name,
                  value: manager.id,
                }))}
                id={VALIDATION_FIELD_MAPPING.manager}
                name={VALIDATION_FIELD_MAPPING.manager}
                searchText={dropdownSearchData.manager}
                onSearch={onDropdownSearchChange}
                placeholder={t("users.list.pageTexts.filters.selectManager")}
                label={t("users.list.pageTexts.filters.manager")}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-md-2 col-sm-4">
              <Dropdown
                values={filters.role && filters.role.toString().trim() ? [filters.role] : []}
                options={(roles || []).map((role: any) => ({
                  label: role.name,
                  value: role.id,
                }))}
                allOptions={(allRoles || []).map((role: any) => ({
                  label: role.name,
                  value: role.id,
                }))}
                id={VALIDATION_FIELD_MAPPING.role}
                name={VALIDATION_FIELD_MAPPING.role}
                searchText={dropdownSearchData.role}
                onSearch={onDropdownSearchChange}
                placeholder={t("users.list.pageTexts.filters.selectRole")}
                label={t("users.list.pageTexts.filters.role")}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-md-2 col-sm-4">
              <Dropdown
                values={filters.status && filters.status.toString().trim() ? [filters.status] : []}
                options={(status || []).map((item: any) => ({
                  label: item.name,
                  value: item.id,
                }))}
                allOptions={(allStatus || []).map((item: any) => ({
                  label: item.name,
                  value: item.id,
                }))}
                id={VALIDATION_FIELD_MAPPING.status}
                name={VALIDATION_FIELD_MAPPING.status}
                searchText={dropdownSearchData.status}
                onSearch={onDropdownSearchChange}
                placeholder={t("users.list.pageTexts.filters.selectStatus")}
                label={t("users.list.pageTexts.filters.status")}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Filter;